import { useEffect, useState } from "react";
import { ngPlus } from "@bd/content";
import { Icon, Logo } from "@bd/ui-kit";
import { host, useGame } from "../store";
import { lastSlot, listSaves, loadGame, type SaveMeta } from "../lib/save";
import { t } from "../i18n";
import { LangSwitch } from "./LangSwitch";

export function TitleScreen() {
  const [name, setName] = useState("Box & Dice Co.");
  const [saves, setSaves] = useState<SaveMeta[]>([]);
  useGame((s) => s.lang); // redraw when the language changes
  useEffect(() => {
    void listSaves().then(setSaves).catch(() => setSaves([]));
  }, []);
  const start = () => {
    const seed = `solo-${Date.now().toString(36)}`;
    host.newGame(seed, name.trim() || "Box & Dice Co.");
    useGame.setState({ onboarding: 0 });
  };
  const resume = async (slot: string) => {
    const g = await loadGame(slot);
    if (g) {
      host.load(g);
      useGame.setState({ onboarding: 4 });
    }
  };
  const last = lastSlot();
  const plus = readPlus();
  const startPlus = () => {
    host.newGame(`plus-${Date.now().toString(36)}`, name.trim() || "Box & Dice Co.", "you", { notebook: plus?.notebook ?? {} });
    useGame.setState({ onboarding: 4 });
  };
  return (
    <div className="title">
      <div className="panel title-card" data-testid="title">
        <LangSwitch />
        <div className="row" title={t("1965. A garage, $8,000 and an idea for a game.")}>
          <Logo size={48} />
          <h1 className="display" style={{ margin: 0, fontSize: "2.4rem" }}>Box &amp; Dice</h1>
        </div>
        <input className="title-name" value={name} maxLength={40} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && start()} aria-label={t("Your company")} title={t("Your company")} placeholder={t("Your company")} data-testid="company-name" />
        <button className="btn primary" onClick={start} data-testid="start"><Icon name="play" size={16} />{t("Start")}</button>
        <button className="btn" onClick={() => useGame.setState({ lobby: true })} title={t("Play with the cousins")} data-testid="play-online"><Icon name="fans" size={18} />{t("Play online")}</button>
        {plus && (
          <div data-testid="ngplus" style={{ display: "contents" }}>
            <button className="btn teal" onClick={startPlus} title={`${ngPlus.title}: ${ngPlus.intro}`} data-testid="start-ngplus"><Icon name="replay" size={16} />{t("New Game+")}</button>
          </div>
        )}
        {saves.length > 0 && (
          <div className="stack" role="group" aria-label={t("Continue")} style={{ gap: 6 }}>
            {saves.slice(0, 4).map((s) => (
              <button key={s.slot} className="btn" style={{ justifyContent: "flex-start" }} onClick={() => resume(s.slot)} title={s.slot === last ? t("· last played") : s.slot === "autosave" ? t("· autosave") : undefined} data-testid={`load-${s.slot}`}>
                <Icon name={s.slot === last ? "play" : s.slot === "autosave" ? "replay" : "save"} size={16} color={s.slot === last ? "var(--coral)" : undefined} />
                <span className="ellipsis">{s.company}</span><span className="num muted" style={{ marginLeft: "auto" }}>{1965 + Math.floor(s.week / 52)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** New Game+ unlocks once a season reaches the 2035 finale (saved by App when it happens). */
function readPlus(): { notebook: Record<string, number> } | null {
  try {
    const raw = localStorage.getItem("bd:ngplus");
    return raw ? (JSON.parse(raw) as { notebook: Record<string, number> }) : null;
  } catch {
    return null;
  }
}
